/**
 * Arbres de compétences par classe.
 * - Branches offensives / défensives / utilitaires.
 * - Coût en points de compétence (PC) et prérequis non-linéaires.
 * - Chaque compétence a un élément, un coût (MP / Stamina), un cooldown et
 *   éventuellement des chances d'appliquer un statut.
 */

const T = {
  attack: 'attack',
  heal: 'heal',
  buff: 'buff',
  debuff: 'debuff',
  dot: 'dot',
  barrier: 'barrier',
  revive: 'revive',
  aoe: 'aoe'
};

const E = {
  physical: 'physical',
  fire: 'fire',
  ice: 'ice',
  lightning: 'lightning',
  holy: 'holy',
  dark: 'dark',
  nature: 'nature',
  earth: 'earth',
  wind: 'wind'
};

function skill(spec: any) {
  const base = {
    target: 'single',
    cost: {},
    cooldown: 0,
    statuses: [],
    req: null,
    skillPoints: 1,
    branch: 'offense',
    minLevel: 1,
    tier: 1,
    ...spec
  };
  if (!spec?.tier) {
    const lvl = base.minLevel || 1;
    base.tier = lvl <= 1 ? 1 : lvl <= 3 ? 2 : lvl <= 6 ? 3 : 4;
  }
  return base;
}

function attack(id: string, name: string, icon: string, element: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.attack, element, affinity, ...overrides });
}

function aoe(id: string, name: string, icon: string, element: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.aoe, element, affinity, target: 'allEnemies', cooldown: 3, ...overrides });
}

function heal(id: string, name: string, icon: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.heal, element: E.holy, affinity, target: 'ally', cooldown: 2, ...overrides });
}

function buff(id: string, name: string, icon: string, status: any, target = 'ally', overrides: any = {}) {
  return skill({ id, name, icon, type: T.buff, element: E.holy, affinity: {}, target, statuses: [{ id: status, chance: 1, duration: 3 }], cooldown: 3, ...overrides });
}

function debuff(id: string, name: string, icon: string, status: any, element: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.debuff, element, affinity, statuses: [{ id: status, chance: 0.75, duration: 3 }], cooldown: 3, ...overrides });
}

function dot(id: string, name: string, icon: string, status: any, element: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.dot, element, affinity, statuses: [{ id: status, chance: 0.8, duration: 4 }], cooldown: 3, ...overrides });
}

function barrier(id: string, name: string, icon: string, target = 'allAllies', overrides: any = {}) {
  return skill({ id, name, icon, type: T.barrier, element: E.holy, affinity: { SAG: 0.5 }, target, effect: { flat: 60 }, cooldown: 4, ...overrides });
}

function charm(id: string, name: string, icon: string, affinity: any, overrides: any = {}) {
  return skill({ id, name, icon, type: T.debuff, element: E.holy, affinity, target: 'single', statuses: [{ id: 'charmed', chance: 0.55, duration: 2 }], cooldown: 4, branch: 'control', ...overrides });
}

export const SKILL_TREES = {
  guerrier: [
    // Niveau 1 : choix initial
    attack('g_strike', 'Frappe Puissante', '💥', E.physical, { FOR: 1.4 }, {
      cost: { stamina: 8 }, desc: 'Frappe directe infligeant des dégâts physiques.'
    }),
    skill({ id: 'g_stance', name: 'Posture Défensive', icon: '🛡️', type: T.buff, element: E.physical, affinity: { FOR: 0.5 },
      cost: { stamina: 10 }, target: 'self', statuses: [{ id: 'defUp', chance: 1, duration: 3 }], cooldown: 4,
      branch: 'defense', minLevel: 1, desc: 'Augmente son armure de 25% pendant 3 tours.'
    }),

    // Niveau 3
    attack('g_shield_bash', 'Coup de Bouclier', '🛡️', E.physical, { FOR: 1.0, DEX: 0.6 }, {
      req: 'g_strike', cost: { stamina: 12 }, cooldown: 3, statuses: [{ id: 'stun', chance: 0.35, duration: 1 }], branch: 'offense',
      desc: 'Frappe lourde qui peut étourdir la cible (35%).'
    }),
    skill({ id: 'g_taunt', name: 'Provocation', icon: '📢', type: T.debuff, element: E.physical, affinity: { CHA: 1.0 },
      req: 'g_stance', cost: { stamina: 10 }, target: 'single', statuses: [{ id: 'atkDown', chance: 1, duration: 2 }], cooldown: 4,
      branch: 'defense', minLevel: 3, desc: 'Affaiblit l\'attaque ennemie et attire son attention.'
    }),

    // Niveau 6
    attack('g_colossus', 'Frappe du Colosse', '🌍', E.earth, { FOR: 2.0 }, {
      req: 'g_shield_bash', cost: { stamina: 18 }, cooldown: 3, statuses: [{ id: 'defDown', chance: 0.5, duration: 2 }], branch: 'offense',
      desc: 'Attaque de terre puissante qui brise l\'armure (50%).'
    }),
    barrier('g_wall', 'Mur d\'Acier', '🧱', 'allAllies', {
      req: 'g_taunt', cost: { stamina: 20 }, effect: { flat: 80 }, cooldown: 5, branch: 'defense', minLevel: 6,
      desc: 'Barrière protectrice sur toute l\'équipe.'
    }),

    // Niveau 10
    attack('g_execute', 'Exécution', '☠️', E.physical, { FOR: 3.2 }, {
      req: 'g_colossus', cost: { stamina: 30 }, cooldown: 5, branch: 'offense', minLevel: 10,
      desc: 'Coup fatal infligeant des dégâts physiques massifs.'
    }),
    skill({ id: 'g_undying', name: 'Champion Indomptable', icon: '👑', type: T.buff, element: E.holy, affinity: { FOR: 0.5 },
      req: 'g_wall', cost: { stamina: 25 }, target: 'self', statuses: [{ id: 'reviveMark', chance: 1, duration: 999 }], cooldown: 6,
      branch: 'defense', minLevel: 10, desc: 'Bénit le guerrier : ressuscite une fois si KO.'
    })
  ],

  mage: [
    // Niveau 1
    attack('m_fireball', 'Boule de Feu', '🔥', E.fire, { INT: 1.5 }, {
      cost: { mp: 12 }, statuses: [{ id: 'burn', chance: 0.4, duration: 3 }], desc: 'Boule de feu explosive, peut brûler (40%).'
    }),
    attack('m_iceshard', 'Éclat de Glace', '❄️', E.ice, { INT: 1.3 }, {
      cost: { mp: 10 }, statuses: [{ id: 'freeze', chance: 0.35, duration: 2 }], branch: 'utility', minLevel: 1,
      desc: 'Projectile glacé qui ralentit la cible (35%).'
    }),

    // Niveau 3
    skill({ id: 'm_barrier', name: 'Barrière Runique', icon: '🔷', type: T.barrier, element: E.ice, affinity: { INT: 0.6 },
      req: 'm_iceshard', cost: { mp: 15 }, target: 'ally', effect: { flat: 60 }, cooldown: 4, branch: 'defense', minLevel: 3,
      desc: 'Barrière magique protégeant un allié.'
    }),
    attack('m_lightning', 'Éclair Arcanique', '⚡', E.lightning, { INT: 1.7, DEX: 0.3 }, {
      req: 'm_fireball', cost: { mp: 16 }, cooldown: 2, statuses: [{ id: 'stun', chance: 0.25, duration: 1 }], branch: 'offense', minLevel: 3,
      desc: 'Foudre rapide avec chance d\'étourdissement (25%).'
    }),

    // Niveau 6
    aoe('m_firestorm', 'Brasier', '🔥', E.fire, { INT: 1.4 }, {
      req: 'm_lightning', cost: { mp: 28 }, cooldown: 4, statuses: [{ id: 'burn', chance: 0.5, duration: 3 }], branch: 'offense', minLevel: 6,
      desc: 'Enflamme tous les ennemis.'
    }),
    debuff('m_sleep', 'Sommeil Profond', '💤', 'sleep', E.dark, { INT: 0.8 }, {
      req: 'm_barrier', cost: { mp: 20 }, cooldown: 5, statuses: [{ id: 'sleep', chance: 0.6, duration: 2 }], branch: 'utility', minLevel: 6,
      desc: 'Endort la cible (60%). Se réveille si attaquée.'
    }),

    // Niveau 10
    aoe('m_meteor', 'Météore', '☄️', E.fire, { INT: 2.8 }, {
      req: 'm_firestorm', cost: { mp: 45 }, cooldown: 6, statuses: [{ id: 'burn', chance: 0.8, duration: 4 }], branch: 'offense', minLevel: 10,
      desc: 'Pluie de météores infligeant d\'immenses dégâts de feu.'
    }),
    skill({ id: 'm_arcane_flow', name: 'Flux Arcanique', icon: '🔮', type: T.buff, element: E.wind, affinity: { INT: 0.5 },
      req: 'm_sleep', cost: { mp: 25 }, target: 'self', statuses: [{ id: 'intUp', chance: 1, duration: 4 }, { id: 'vitUp', chance: 1, duration: 4 }], cooldown: 5,
      branch: 'utility', minLevel: 10, desc: 'Booster magie et vitesse du mage.'
    })
  ],

  voleur: [
    // Niveau 1
    attack('v_stab', 'Coup Bas', '🔪', E.physical, { DEX: 1.4, FOR: 0.3 }, {
      cost: { stamina: 8 }, desc: 'Frappe rapide et précise.'
    }),
    skill({ id: 'v_dodge', name: 'Pas de l\'Ombre', icon: '👤', type: T.buff, element: E.wind, affinity: { DEX: 0.5 },
      cost: { stamina: 10 }, target: 'self', statuses: [{ id: 'vitUp', chance: 1, duration: 3 }], cooldown: 4, branch: 'utility', minLevel: 1,
      desc: 'Augmente la vitesse pour esquiver.'
    }),

    // Niveau 3
    dot('v_poison', 'Lame Empoisonnée', '🧪', 'poison', E.nature, { DEX: 1.0, INT: 0.4 }, {
      req: 'v_stab', cost: { stamina: 12 }, cooldown: 3, branch: 'offense', minLevel: 3,
      desc: 'Empoisonne lentement l\'ennemi.'
    }),
    debuff('v_smoke', 'Bombe Fumigène', '💣', 'defDown', E.wind, { DEX: 0.8 }, {
      req: 'v_dodge', cost: { stamina: 14 }, cooldown: 3, statuses: [{ id: 'atkDown', chance: 0.5, duration: 2 }], branch: 'utility', minLevel: 3,
      desc: 'Fumigène réduisant armure et attaque.'
    }),

    // Niveau 6
    attack('v_shadow_strike', 'Frappe de l\'Ombre', '🌑', E.dark, { DEX: 1.8 }, {
      req: 'v_poison', cost: { stamina: 18 }, cooldown: 3, statuses: [{ id: 'bleed', chance: 0.5, duration: 3 }], branch: 'offense', minLevel: 6,
      desc: 'Attaque ténébreuse infligeant des saignements.'
    }),
    attack('v_backstab', 'Attaque Sournoise', '🗡️', E.physical, { DEX: 2.0, FOR: 0.2 }, {
      req: 'v_smoke', cost: { stamina: 16 }, cooldown: 3, branch: 'offense', minLevel: 6,
      desc: 'Coup critique quasi-garanti contre une cible affaiblie.'
    }),

    // Niveau 10
    aoe('v_blade_dance', 'Danse des Lames', '⚔️', E.physical, { DEX: 2.2 }, {
      req: 'v_shadow_strike', cost: { stamina: 28 }, cooldown: 5, branch: 'offense', minLevel: 10,
      desc: 'Enchaînement rapide sur tous les ennemis.'
    }),
    buff('v_shadow_form', 'Forme d\'Ombre', '🌫️', 'vitUp', 'self', {
      req: 'v_backstab', cost: { stamina: 22 }, cooldown: 5, statuses: [{ id: 'atkUp', chance: 1, duration: 3 }, { id: 'vitUp', chance: 1, duration: 3 }], branch: 'utility', minLevel: 10,
      desc: 'Boost vitesse et attaque du voleur.'
    })
  ],

  pretre: [
    // Niveau 1
    heal('p_heal', 'Soins Divins', '✨', { SAG: 1.6 }, {
      cost: { mp: 10 }, desc: 'Soigne un allié.'
    }),
    attack('p_smite', 'Châtiment', '⚡', E.holy, { SAG: 1.2, INT: 0.4 }, {
      cost: { mp: 10 }, branch: 'offense', minLevel: 1, desc: 'Frappe sacrée infligeant des dégâts lumineux.'
    }),

    // Niveau 3
    buff('p_bless', 'Bénédiction', '😇', 'defUp', 'ally', {
      req: 'p_heal', cost: { mp: 14 }, cooldown: 3, branch: 'defense', minLevel: 3,
      desc: 'Augmente l\'armure d\'un allié.'
    }),
    debuff('p_blind', 'Lumière Aveuglante', '✨', 'atkDown', E.holy, { SAG: 0.8 }, {
      req: 'p_smite', cost: { mp: 14 }, cooldown: 3, statuses: [{ id: 'atkDown', chance: 1, duration: 3 }], branch: 'offense', minLevel: 3,
      desc: 'Réduit l\'attaque ennemie.'
    }),

    // Niveau 6
    heal('p_regen', 'Régénération', '💚', { SAG: 1.0 }, {
      req: 'p_bless', cost: { mp: 18 }, cooldown: 3, statuses: [{ id: 'regen', chance: 1, duration: 4 }], branch: 'support', minLevel: 6,
      desc: 'Soigne un allié sur plusieurs tours.'
    }),
    aoe('p_holy_nova', 'Nova Sacrée', '☀️', E.holy, { SAG: 1.5, INT: 0.5 }, {
      req: 'p_blind', cost: { mp: 24 }, cooldown: 4, branch: 'offense', minLevel: 6,
      desc: 'Explosion de lumière sur tous les ennemis.'
    }),

    // Niveau 10
    heal('p_sanctuary', 'Sanctuaire', '👼', { SAG: 2.8 }, {
      req: 'p_regen', cost: { mp: 32 }, target: 'allAllies', cooldown: 5, branch: 'support', minLevel: 10,
      desc: 'Soigne massivement toute l\'équipe.'
    }),
    skill({ id: 'p_revive', name: 'Résurrection', icon: '🕊️', type: T.revive, element: E.holy, affinity: { SAG: 2.0 },
      req: 'p_holy_nova', cost: { mp: 40 }, target: 'ally', effect: { pctHp: 0.40 }, cooldown: 6, branch: 'support', minLevel: 10,
      desc: 'Ressuscite n’importe quel combattant KO avec exactement 40 % de ses PV max.'
    })
  ],

  paladin: [
    // Niveau 1
    attack('pal_strike', 'Frappe Sacrée', '🔨', E.holy, { FOR: 1.0, SAG: 0.6 }, {
      cost: { mp: 8, stamina: 6 }, desc: 'Frappe hybride sacrée.'
    }),
    skill({ id: 'pal_aura', name: 'Aura de Foi', icon: '🛡️', type: T.buff, element: E.holy, affinity: { SAG: 0.5 },
      cost: { mp: 10 }, target: 'allAllies', statuses: [{ id: 'defUp', chance: 1, duration: 3 }], cooldown: 4, branch: 'defense', minLevel: 1,
      desc: 'Augmente l\'armure de toute l\'équipe.'
    }),

    // Niveau 3
    heal('pal_light', 'Lumière Salvatrice', '✨', { SAG: 1.4 }, {
      req: 'pal_strike', cost: { mp: 14 }, branch: 'support', minLevel: 3,
      desc: 'Soigne modérément un allié.'
    }),
    debuff('pal_judgment', 'Jugement', '⚖️', 'defDown', E.holy, { SAG: 0.8, FOR: 0.4 }, {
      req: 'pal_aura', cost: { mp: 12 }, cooldown: 3, statuses: [{ id: 'defDown', chance: 1, duration: 3 }], branch: 'offense', minLevel: 3,
      desc: 'Réduit l\'armure ennemie par jugement divin.'
    }),

    // Niveau 6
    barrier('pal_divine_shield', 'Bouclier Divin', '🛡️', 'ally', {
      req: 'pal_light', cost: { mp: 20 }, effect: { flat: 100 }, cooldown: 4, branch: 'defense', minLevel: 6,
      desc: 'Barrière sacrée puissante sur un allié.'
    }),
    attack('pal_hammer', 'Marteau de Colère', '🔨', E.holy, { FOR: 2.0, SAG: 0.5 }, {
      req: 'pal_judgment', cost: { mp: 18, stamina: 10 }, cooldown: 3, statuses: [{ id: 'stun', chance: 0.4, duration: 1 }], branch: 'offense', minLevel: 6,
      desc: 'Coup de marteau sacré pouvant étourdir (40%).'
    }),

    // Niveau 10
    skill({ id: 'pal_savior', name: 'Messie', icon: '🕊️', type: T.buff, element: E.holy, affinity: { SAG: 1.0 },
      req: 'pal_divine_shield', cost: { mp: 30 }, target: 'allAllies', statuses: [{ id: 'reviveMark', chance: 1, duration: 999 }], cooldown: 6,
      branch: 'support', minLevel: 10, desc: 'Bénit toute l\'équipe : ressuscite une fois si KO.'
    }),
    aoe('pal_holy_wrath', 'Courroux Céleste', '☀️', E.holy, { FOR: 1.5, SAG: 1.0 }, {
      req: 'pal_hammer', cost: { mp: 32, stamina: 14 }, cooldown: 5, statuses: [{ id: 'burn', chance: 0.5, duration: 3 }], branch: 'offense', minLevel: 10,
      desc: 'Vague de lumière sacrée brûlant les ennemis.'
    })
  ],

  barde: [
    // Niveau 1
    attack('b_dissonance', 'Note Discordante', '🎵', E.wind, { CHA: 1.2, INT: 0.4 }, {
      cost: { mp: 10 }, desc: 'Onde sonore perçante infligeant des dégâts magiques.'
    }),
    heal('b_soothing', 'Chant Apaisant', '💚', { CHA: 1.0, SAG: 0.5 }, {
      cost: { mp: 12 }, target: 'ally', cooldown: 2, desc: 'Mélodie douce qui soigne un allié.'
    }),

    // Niveau 3
    buff('b_heroic', 'Ballade Héroïque', '🎺', 'atkUp', 'allAllies', {
      req: 'b_dissonance', cost: { mp: 16 }, cooldown: 4, branch: 'support', minLevel: 3,
      desc: 'Inspire l\'équipe et augmente ses dégâts.'
    }),
    charm('b_charm', 'Mélodie de Charme', '🎶', { CHA: 1.0 }, {
      req: 'b_soothing', cost: { mp: 18 }, minLevel: 3,
      desc: 'Charmer un ennemi (55%). Il attaquera ses alliés ou se soignera s\'il est seul.'
    }),

    // Niveau 6
    skill({ id: 'b_war_cry', name: 'Cri de Guerre', icon: '📢', type: T.buff, element: E.holy, affinity: { CHA: 0.6 },
      req: 'b_heroic', cost: { mp: 20 }, target: 'allAllies', statuses: [{ id: 'vitUp', chance: 1, duration: 3 }], cooldown: 4,
      branch: 'support', minLevel: 6, desc: 'Booste la vitesse de toute l\'équipe.'
    }),
    skill({ id: 'b_allure', name: 'Allure Captivante', icon: '✨', type: T.buff, element: E.holy, affinity: { CHA: 0.8 },
      req: 'b_charm', cost: { mp: 22 }, target: 'self', statuses: [{ id: 'allure', chance: 1, duration: 4 }], cooldown: 5,
      branch: 'support', minLevel: 6, desc: 'Augmente le charisme et la puissance des prochains chants.'
    }),

    // Niveau 10
    heal('b_symphony_heal', 'Symphonie de Soin', '🌈', { CHA: 1.4, SAG: 0.6 }, {
      req: 'b_war_cry', cost: { mp: 36 }, target: 'allAllies', cooldown: 5, branch: 'support', minLevel: 10,
      desc: 'Un chant puissant qui soigne tous les alliés.'
    }),
    charm('b_betrayal', 'Ode à la Trahison', '🎭', { CHA: 2.0 }, {
      req: 'b_allure', cost: { mp: 40 }, minLevel: 10, statuses: [{ id: 'charmed', chance: 0.75, duration: 3 }], cooldown: 6,
      desc: 'Charme puissant (75%) : la cible trahit ses alliés pendant 3 tours.'
    })
  ]
};

export function getSkillTree(classId: string) {
  return (SKILL_TREES as Record<string, any>)[classId] || [];
}

export function getSkillById(classId: string, skillId: string) {
  return (SKILL_TREES as Record<string, any>)[classId]?.find((s: any) => s.id === skillId) || null;
}

export function getUnlockedSkills(classId: string, level: number, unlockedIds: string[] = []) {
  return (SKILL_TREES as Record<string, any>)[classId]?.filter((s: any) =>
    s.minLevel <= level && (s.req === null || unlockedIds.includes(s.req))
  ) || [];
}
